import * as Sentry from '@sentry/vue';
import type { App } from 'vue';
import type { Router } from 'vue-router';

/**
 * Initializes Sentry for the admin SPA.
 * No-op when VITE_SENTRY_DSN is not configured (local development).
 *
 * Policy: only *unexpected* errors (bugs) are captured. Expected API
 * errors (4xx with a server-provided message) are surfaced as toasts
 * and must NOT be reported here — see src/api/errors.ts.
 */
export function initSentry(app: App, router: Router): void {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (!dsn) return;

  const tracesSampleRate = Number.parseFloat(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE ?? '0');

  Sentry.init({
    app,
    dsn,
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT || import.meta.env.MODE,
    release: import.meta.env.VITE_SENTRY_RELEASE || undefined,
    sendDefaultPii: false,
    integrations: [Sentry.browserTracingIntegration({ router })],
    tracesSampleRate: Number.isFinite(tracesSampleRate) ? tracesSampleRate : 0,
  });
}

interface SentryUser {
  id?: string | null;
  role?: string | null;
}

/** Attach the signed-in staff user (id + role only, no PII). Pass null on logout. */
export function setSentryUser(user: SentryUser | null): void {
  if (!Sentry.getClient()) return;
  Sentry.setUser(
    user
      ? {
          id: user.id ?? undefined,
          role: user.role ?? undefined,
        }
      : null,
  );
}
